<template>
  <div class="multiple-choice-editor">
    <Draggable
      v-model="options"
      item-key="id"
      :force-fallback="true"
      :disabled="isLocked(id)"
      class="multiple-choice-editor__list"
      handle=".multiple-choice-editor__option-handle"
      tag="ol"
      @start="onDragStart"
      @end="onDragEnd"
    >
      <template #item="{ element: option, index }">
        <li class="multiple-choice-editor__option">
          <span
            class="multiple-choice-editor__option-handle"
            v-if="!isLocked(id)"
          >
            ⠿
          </span>

          <!-- Radio select -->
          <NebulaRadio
            v-if="type === 'single'"
            class="multiple-choice-editor__radio-label"
          >
            <template #radio>
              <span class="nebula-screenreader-only">
                {{ option.label }},
                {{ option.isCorrect ? $t('correct answer') : $t('incorrect answer') }}
              </span>
              <input
                class="nebula-radio"
                v-model="moduleData.options.valid_options[0]"
                :name="`${moduleData.id}--options`"
                type="radio"
                :value="index"
                :disabled="isLocked(id)"
                @change="updateState"
              >
            </template>
          </NebulaRadio>

          <!-- Checkbox select -->
          <NebulaCheckbox
            v-else
            class="multiple-choice-editor__checkbox-label"
            size="s"
          >
            <span class="nebula-screenreader-only">
              {{ option.label }},
              {{ option.isCorrect ? $t('correct answer') : $t('incorrect answer') }}
            </span>
            <template #checkbox>
              <input
                v-model="moduleData.options.valid_options"
                class="nebula-checkbox"
                type="checkbox"
                :name="`${moduleData.id}--options`"
                :value="index"
                :disabled="isLocked(id)"
                @change="updateState"
              >
            </template>
          </NebulaCheckbox>

          <!-- Input for option text -->
          <div class="multiple-choice-editor__input-wrap">
            <NebulaInput
              :aria-label="$t('Answer text for option %(optionNumber)d', {
                optionNumber: index + 1,
              })"
              class="multiple-choice-editor__option-text-input"
              :disabled="isLocked(id)"
              :placeholder="$t('Enter text here…')"
              size="s"
              type="text"
              :value="option.label"
              @blur="updateState"
              @input="updateText(index, $event)"
            />
          </div>

          <div>
            <NebulaButton
              :aria-label="$t('Remove option')"
              class="multiple-choice-editor__delete-button"
              :disabled="options.length <= 2 || isLocked(id)"
              icon-left="trash"
              size="s"
              type="flat"
              @click="removeItem(index)"
            />
          </div>
        </li>
      </template>
    </Draggable>
    <NebulaButton
      class="multiple-choice-editor__add-button"
      :disabled="options.length >= 10 || isLocked(id)"
      icon-left="add"
      :text="$t('Add Option')"
      type="flat"
      @click="addItem"
    />
  </div>
</template>

<script>
import {
  NebulaButton, NebulaCheckbox, NebulaInput, NebulaRadio,
} from '@discoveryedu/nebula-components';
import { throttle, toInteger } from 'lodash-es';
import Draggable from 'vuedraggable';
import { tokens as nebulaTokens } from '@discoveryedu/nebula-core/dist/data/nebula_tokens.json';
import { mapState } from 'pinia';
import {
  useEditorStore,
} from '@/stores';

export default {
  name: 'MultipleChoiceEditor',
  components: {
    NebulaButton,
    NebulaCheckbox,
    NebulaInput,
    NebulaRadio,
    Draggable,
  },
  props: {
    id: {
      type: String,
      required: true,
    },
    canvasName: {
      type: String,
      required: true,
    },
    moduleData: {
      type: Object, // module data from the store
      required: true,
    },
    type: {
      type: String, // out of ['single', 'multiple']
      required: true,
    },
    scale: {
      type: Number,
      default: 1,
    },
  },
  emits: ['update-module'],
  data() {
    return {
      isDirty: false,
      dragging: false,
      zoomContent: null,
      space1x: parseInt(nebulaTokens.space['1x'], 10),
    };
  },
  created() {
    this.onDragMove = throttle((event) => {
      if (this.dragging) {
        /*
          Need to override the positioning of the ghost element while dragging since
          vuedraggable doesn't handle this properly when drag elements have been
          scaled using CSS transform.
        */
        const ghost = document.querySelector('.multiple-choice-editor .sortable-drag');
        if (ghost && this.zoomContent) {
          const zoomContentRect = this.zoomContent.getBoundingClientRect();
          const ghostRect = ghost.getBoundingClientRect();
          ghost.style.transform = 'none';
          ghost.style.left = `${((event.clientX - zoomContentRect.left) / this.scale) - this.space1x}px`;
          ghost.style.top = `${(event.clientY - zoomContentRect.top - (ghostRect.height / 2)) / this.scale}px`;
        }
      }
    }, 1);
  },
  computed: {
    ...mapState(useEditorStore, [
      'isLocked',
    ]),
    options: {
      get() {
        const validOptions = this.moduleData.options.valid_options.map(toInteger);
        return this.moduleData.options.options.map((option, i) => (
          {
            label: option,
            isCorrect: validOptions.indexOf(i) !== -1,
            id: i,
          }
        ));
      },
      set(options) {
        this.updateModuleDataOptions(options);
        this.updateState();
      },
    },
  },
  beforeUnmount() {
    if (this.isDirty) this.updateState();
  },
  methods: {
    onDragStart() {
      this.dragging = true;
      document.addEventListener('mousemove', this.onDragMove);
      this.zoomContent = this.$el.closest('.editor__zoom-content');
    },
    onDragEnd() {
      this.dragging = false;
      document.removeEventListener('mousemove', this.onDragMove);
    },
    updateModuleDataOptions(options) {
      this.moduleData.options.options = options.map((opt) => opt.label);
      this.moduleData.options.valid_options = options
        .map((opt, optIdx) => (opt.isCorrect ? optIdx : null)) // grab the index before filtering
        .filter((opt) => opt !== null);
    },
    addItem() {
      const newIndex = this.moduleData.options.options.length;

      // Prevent having more than 10 options
      if (newIndex >= 10) return;

      // Update the module in state, plus a new empty option
      this.moduleData.options.options.push(this.$t('Option %(incrementedIndex)s', { incrementedIndex: newIndex + 1 }));
      this.updateState();
    },
    removeItem(i) {
      const options = [...this.options];

      // Remove the clicked option and update the valid indices
      options.splice(i, 1);

      // Update options and valid_options in module data
      this.updateModuleDataOptions(options);

      // Update the module in state
      this.updateState();
    },
    updateState() {
      this.$emit('update-module');
      this.isDirty = false;
    },
    updateText(i, newText) {
      // While typing, update local module data only
      this.moduleData.options.options[i] = newText;
      this.isDirty = true;
    },
  },
};
</script>

<style lang="stylus">
.multiple-choice-editor {
  &__list {
    margin: 0 0 $nebula-space-2x 0;
    padding: 0;
  }

  &__option {
    align-items: center;
    display: flex;
    inset-inline-start: $nebula-space-2x * -1;
    list-style-type: none;
    margin: 0 0 $nebula-space-1x 0;
    padding-inline-start: $nebula-space-2x;
    position: relative;
    width: "calc(100% + %s)" % $nebula-space-2x;

    &-handle {
      cursor: move;
      inset-inline-start: 2px;
      position: absolute;
      top: 4px;
      visibility: hidden;
    }

    &:hover {
      .multiple-choice-editor__option-handle {
        visibility: visible;
      }
    }
  }

  &__checkbox-label.nebula-checkbox__container
  &__radio-label.nebula-radio__container {
    margin-block: 0;
    margin-inline-end: $nebula-space-1x;
  }

  &__input-wrap {
    flex-grow: 1;
  }

  &__option-text-input.nebula-input {
    margin: 0;
  }

  &__delete-button.nebula-button {
    align-items: center;
    display: flex;
    height: 30px; // match dimensions of dense-style Comet input
    justify-content: center;
    margin-inline: $nebula-space-half 0;
    padding: 0;
    width: 30px;

    &:disabled svg {
      fill: rgba($nebula-color-platform-interactive-800, .3);
    }
  }

  html[dir="rtl"] & {
    &__add-button {
      margin: 0;
    }
  }
}
</style>
